import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

// Only allow absolute http(s) destinations — blocks javascript:/data: (XSS) and malformed URLs.
function safeUrl(u) {
  if (!u) return "";
  try {
    const parsed = new URL(u);
    return (parsed.protocol === "http:" || parsed.protocol === "https:") ? parsed.href : "";
  } catch { return ""; }
}

// Persistent, opaque guest id so a purchase can be traced back to this shopper (never PII).
function guestSid() {
  try {
    let s = localStorage.getItem("sfl_aff_sid");
    if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())); localStorage.setItem("sfl_aff_sid", s); }
    return s;
  } catch { return ""; }
}

// Branded hand-off screen: mints a trackable deep link, then redirects. Never dead-ends — on any
// failure or slowness it sends the shopper to the original partner URL.
export default function AffiliateOut() {
  const [sp] = useSearchParams();
  const target = safeUrl(sp.get("u") || "");
  const source = sp.get("src") || "cuelinks";
  const [merchant] = useState(sp.get("m") || "our partner");
  const done = useRef(false);

  useEffect(() => {
    if (!target) { window.location.replace("/deals"); return; }
    const go = (url) => { if (done.current) return; done.current = true; window.location.replace(url); };
    const startedAt = Date.now();
    const settle = (url) => {
      const safe = safeUrl(url) || target;
      const wait = Math.max(0, 1000 - (Date.now() - startedAt)); // branded moment ≥ ~1s
      setTimeout(() => go(safe), wait);
    };
    // Hard safety net: never hang — fall back to the original URL if generation is slow.
    const safety = setTimeout(() => go(target), 7000);
    api.post("/affiliate/deeplink", { url: target, source, sid: guestSid() })
      .then((r) => { clearTimeout(safety); settle(r.data?.url || target); })
      .catch(() => { clearTimeout(safety); settle(target); });
    return () => clearTimeout(safety);
  }, [target, source]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#0b0a1f] px-6 text-center text-white" data-testid="affiliate-out">
      <Logo variant="sm" />
      <Loader2 className="h-10 w-10 animate-spin text-blue-400" />
      <div>
        <p className="text-lg font-semibold" data-testid="affiliate-out-title">Taking you to {merchant}…</p>
        <p className="mt-1 text-sm text-white/60">Securing your deal and applying tracking. One moment.</p>
      </div>
      {target && <a href={target} rel="nofollow sponsored noopener noreferrer" className="text-xs text-white/40 underline" data-testid="affiliate-out-manual">Not redirected? Continue</a>}
    </div>
  );
}
